import React, { useMemo } from 'react';
import * as BuilderUI from '../BuilderUI';
import KubitTooltip from '../../KubitTooltip';
import { ITooltipOption } from '../../KubitTooltip/globalTooltips';
import {
  SingleSelect,
  SingleSelectOption,
  SingleSelectLabel,
} from '../../Dropdown/SingleSelect';
import { IStyled } from '../../../styles/types';
import { Placement, Strategy } from '@floating-ui/react';

interface IBuilderSelectMenuOption {
  text: string;
  value: string;
  tooltip?: ITooltipOption;
  disabled?: boolean;
}

interface IBuilderSelectMenu extends IStyled {
  /**
   * If label is not provided, the label will be hidden, only show the dropdown part
   */
  label?: string;
  options: IBuilderSelectMenuOption[];
  onChange: (selection: string) => void;
  value?: string;
  disabled?: boolean;
  maxMenuHeight?: number;
  tooltipOption?: ITooltipOption;
  hideCaret?: boolean;
  strategy?: Strategy;
  placement?: Placement;
}

const BuilderSelectMenu = ({
  label,
  options,
  value,
  onChange,
  className,
  disabled,
  maxMenuHeight,
  tooltipOption,
  hideCaret,
  strategy,
  placement,
}: IBuilderSelectMenu) => {
  const selectedValue = useMemo(() => {
    if (!value) {
      return '';
    }

    return options.find((option) => option.value === value)?.text ?? '';
  }, [options, value]);

  return (
    <BuilderUI.BuilderFieldContainer className={className}>
      {label && (
        <KubitTooltip {...(tooltipOption || {})} noOpenDelay={true}>
          <BuilderUI.BuilderFieldLabel $marginRight={hideCaret ? '4px' : '8px'}>
            {label}
          </BuilderUI.BuilderFieldLabel>
        </KubitTooltip>
      )}
      <SingleSelect
        trigger={
          <SingleSelectLabel
            hideCaret={hideCaret}
            isAlwaysActive={true}
            isDisabled={disabled}
          >
            {selectedValue}
          </SingleSelectLabel>
        }
        disabled={disabled}
        menuMaxHeight={maxMenuHeight}
        placement={placement || 'bottom-start'}
        strategy={strategy}
      >
        {options.map((option) => (
          <SingleSelectOption
            key={option.value}
            label={option.text}
            isActive={option.value === value}
            tooltipOption={option?.tooltip}
            tooltipPlacement="right"
            disabled={option?.disabled}
            onClick={() => onChange(option.value)}
          />
        ))}
      </SingleSelect>
    </BuilderUI.BuilderFieldContainer>
  );
};

export default BuilderSelectMenu;
